import { useEffect, useMemo, useRef, useState } from "react";
import { api, photoUrl, viewerId } from "../lib/api";

// Admin-controlled "featured" promo banner for a category landing view.
// Same rotation/tracking as the homepage marketing banner, but half the height.
export function CategoryBanner({ category, hero = false, fallback = null, children = null }) {
  const [marketing, setMarketing] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    setLoaded(false);
    if (!category || category === "all") { setMarketing(null); setLoaded(true); return; }
    api
      .get(`/site/marketing-banner?category=${encodeURIComponent(category)}&viewer=${encodeURIComponent(viewerId())}`)
      .then((r) => setMarketing(r.data))
      .catch(() => setMarketing(null))
      .finally(() => setLoaded(true));
  }, [category]);

  const banners = marketing?.banners || [];
  const seq = useMemo(() => {
    const s = [];
    banners.forEach((b, i) => { const w = Math.max(1, Math.min(10, b.weight || 1)); for (let k = 0; k < w; k++) s.push(i); });
    return s.length ? s : banners.map((_, i) => i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketing]);

  useEffect(() => {
    setIdx(0);
    if (seq.length <= 1) return;
    const t = setInterval(() => { if (!pausedRef.current) setIdx((i) => (i + 1) % seq.length); }, 6000);
    return () => clearInterval(t);
  }, [seq.length]);

  if (!loaded) return null;
  if (!marketing?.active || !banners.length) return fallback;
  const activeIdx = seq.length ? seq[idx % seq.length] : 0;
  const cur = banners[activeIdx];
  if (!cur) return fallback;

  const track = () => { if (cur.id) api.post("/site/marketing-banner/click", { banner_id: cur.id }).catch(() => {}); };
  const imgs = (
    <>
      <img src={photoUrl(cur.mobile_image || cur.image)} alt={cur.alt || "Featured"} data-testid="category-marketing-img-mobile" className={`w-full ${hero ? "aspect-[8/5]" : "aspect-[16/5]"} object-cover sm:hidden`} />
      <img src={photoUrl(cur.image)} alt={cur.alt || "Featured"} data-testid="category-marketing-img-desktop" className={`w-full ${hero ? "aspect-[6/1]" : "aspect-[12/1]"} object-cover hidden sm:block`} />
    </>
  );

  return (
    <section className={hero ? "mt-4" : "mt-5"} data-testid={hero ? "lf-admin-banner" : "category-marketing-banner"}
      onMouseEnter={() => { pausedRef.current = true; }} onMouseLeave={() => { pausedRef.current = false; }}>
      {cur.link ? (
        <a href={cur.link} target="_blank" rel="noopener noreferrer" onClick={track} data-testid="category-marketing-link"
          className="block rounded-2xl overflow-hidden border border-slate-200/80 card-hover">
          {imgs}
        </a>
      ) : (
        <div className="rounded-2xl overflow-hidden border border-slate-200/80" data-testid="category-marketing-static">{imgs}</div>
      )}
      {seq.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2" data-testid="category-banner-dots">
          {banners.map((b, i) => (
            <span key={b.id || i} className={`h-1.5 rounded-full transition-all ${activeIdx === i ? "w-5 bg-[#F5551E]" : "w-1.5 bg-slate-300"}`} />
          ))}
        </div>
      )}
      {children}
    </section>
  );
}
